import { z } from 'zod'
import { useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import FormWrapper from 'app/frontend/base/components/FormWrapper'
import SelectField from 'app/frontend/base/components/form-fields/SelectField'
import SettingField from 'app/frontend/base/components/form-fields/SettingField'
import { useGuild } from 'app/frontend/base/config/context/guild'
import { zSetting } from 'app/frontend/base/helpers/forms'
import { useGameVersion } from 'app/frontend/base/hooks/useGameVersion'
import LinkedGuildsTable, {
  LinkedGuildRow,
  linkedGuildIdsFromRows,
  linkedGuildRowsFor,
  linkedGuildRowsSchema,
} from '../LinkedGuildsTable'

const formSchema = z.object({
  defaultTeamId: z.string().nullable(),
  timezone: z.string(),
  settings: z.object({
    guildNeighborhoodType: zSetting(z.enum(['guild', 'charter'])),
    displayApplyButtonInOverview: zSetting(z.boolean()),
    housingPageDisplay: zSetting(
      z.enum([
        'guild_and_team_pages',
        'guild_page_only',
        'team_pages_only',
        'nowhere',
      ]),
    ),
  }),
  linkedGuildRows: linkedGuildRowsSchema,
})

type GuildSettingsFormEntity = wowaudit.Guild & {
  defaultTeamId: string | null
  linkedGuildRows: LinkedGuildRow[]
}

const GuildSection = () => {
  const gameVersion = useGameVersion()
  const { guild } = useGuild()
  const queryClient = useQueryClient()

  const timeZoneOptions = useMemo(() => {
    return Intl.supportedValuesOf('timeZone')
  }, [])

  if (!guild) return null

  return (
    <FormWrapper<GuildSettingsFormEntity>
      key={guild.id}
      formSchema={formSchema}
      entityType='guild'
      entity={{
        ...guild,
        defaultTeamId: guild.defaultTeam?.id.toString() ?? null,
        linkedGuildRows: linkedGuildRowsFor(guild),
      }}
      transformValues={({ linkedGuildRows, ...values }) => ({
        ...values,
        defaultTeamId: values.defaultTeamId
          ? Number(values.defaultTeamId)
          : null,
        linkedGuildIds: linkedGuildIdsFromRows(linkedGuildRows),
      })}
      onSave={(form, data) => {
        form.reset({
          ...data,
          defaultTeamId: data.defaultTeam?.id?.toString() ?? null,
          linkedGuildRows: linkedGuildRowsFor(data as wowaudit.Guild),
        })
        for (const queryKey of [
          ['guildLinkCandidates'],
          ['guildTeamData'],
          ['guildOverviewData'],
        ]) {
          queryClient.invalidateQueries({ queryKey })
        }
      }}
    >
      <SelectField
        name='defaultTeamId'
        options={guild.teamReferences.map((team) => ({
          label: team.name,
          value: team.id.toString(),
        }))}
        customPlaceholder='No default team'
        transformOptions={false}
        isClearable={true}
      />

      <SelectField
        name='timezone'
        options={timeZoneOptions}
        isSearchable={true}
      />

      {gameVersion.has('housing') ? (
        <>
          <SettingField name='guildNeighborhoodType' />
          <SettingField name='housingPageDisplay' />
        </>
      ) : null}

      <SettingField name='displayApplyButtonInOverview' layout='box' />

      <div className='mt-10'>
        <h4>Linked guilds</h4>

        <p className='mt-6 mb-8'>
          Teams of linked guilds are shown in the team switcher and on the teams
          overview of this guild, and the other way around. This is useful when
          your guild is active in multiple game versions. You can only link
          guilds that you have admin access to.
        </p>

        <LinkedGuildsTable />
      </div>
    </FormWrapper>
  )
}

export default GuildSection
